<template>
    <ContentOrLoading>
        <MainContentSearchable
            v-if="subscriptores"
            title="Listado de subscriptores" 
            id="subscriptores" 
            v-model="filters" 
            :meta="loading ? {} : subscriptores.meta" 
            :disabled="loading" 
            @updated="debouncedGetSubscriptores"
        >
            <template v-slot:extra-options>
                <b-col cols="12" lg="5">
                    <div class="form-group">
                        <label for="select_grupos">Buscar por grupos:</label>
                        <multiselect
                            id="select_grupos"
                            v-model="filterGrupos"
                            placeholder="Buscar por grupos"
                            :options="grupos.map(grupo => grupo.id)"
                            :custom-label="(id) => grupos.find(grupo => grupo.id === id).nombre"
                            :multiple="true"
                            @input="getSubscriptores"
                        />
                    </div>                    
                </b-col>
            </template>

            <template v-slot:header>
                <h3 class="card-title pt-2 text-center">Listado de subscriptores</h3>
                <br/><br/>
                <div class="card-tools mb-3">
                    <b-button class="mr-3" @click="$modal.show('exportar-subscriptores')">
                        Exportar
                    </b-button>
                    <ModalComponent
                        name="exportar-subscriptores"
                        title="Exportar subscriptores"
                        @close="exportado = false"
                    >
                        <b-row>
                            <b-col cols="12" v-if="! exportado">
                                <b-form-select
                                    v-model="exportFormat"
                                    :options="[
                                        { value: 'excel', text: 'Excel' },
                                        { value: 'pdf', text: 'Pdf' }
                                    ]"
                                    class="mb-3"
                                />
                            </b-col>
                            <b-col cols="12" v-if="! exportado">
                                <b-button @click="exportarSubscriptores">
                                    Exportar
                                </b-button>
                            </b-col>
                            <b-col v-if="exportado">
                                <b-alert show variant="success" class="mb-0">
                                    <h4>Los subscriptores han sido exportados correctamente.</h4>
                                    <p>Recibirás una notificación con el enlace para descargar el archivo.</p>
                                </b-alert>
                            </b-col>
                        </b-row>
                    </ModalComponent>
                    <router-link 
                        :to="{ name: 'back.emails.subscriptores.crear' }" 
                        class="btn btn-primary" 
                        :class="{ 'disabled': loading }" 
                        id="crear-subscriptor"
                    >
                        Crear subscriptor
                    </router-link>
                </div>
            </template>
    
            <template v-slot:content>
                <b-row>
                    <b-col cols="12">
                        <ContainerTable :data="subscriptores.data" :responsive="esMovil">
                            <b-table hover :items="subscriptores.data" :fields="fields" :busy="loading">
                                <template #cell(activo)="{ item }">
                                    <span v-if="item.activo" class="mr-2"><i class="text-green fas fa-check-circle"></i></span>
                                    <span v-else class="mr-2"><i class="text-danger fas fa-times-circle"></i></span>
                                </template>
                                <template #cell(opciones)="{ item, index }">
                                    <OpcionesDropdownTableComponent 
                                        :lastIndex="subscriptores.data.length > 3 && index >= subscriptores.data.length - 3"
                                        :options="[
                                            { to: { name: 'back.emails.subscriptores.show', params: { subscriptor_id: item.id } }, label: 'Ver'},
                                            { to: { name: 'back.emails.subscriptores.edit', params: { subscriptor_id: item.id } }, label: 'Editar'},
                                            { type: 'action', action:() => showModal('borrar-' + item.id), label: 'Borrar'},
                                        ]"
                                    />
                                    <BorrarModalComponent
                                        confirmButton="¿Está segur@ de borrar este subscriptor?"
                                        :name="'borrar-' + item.id"
                                        :title="'Borrar subscriptor ' + item.nombre"
                                        @close="() => closeModal('borrar-' + item.id, false)"
                                        @confirm="() => eliminarSubscriptor(item.id)"
                                    />
                                </template>
                            </b-table>
                        </ContainerTable>
                    </b-col>
                </b-row>
            </template>
        </MainContentSearchable>
    </ContentOrLoading>
</template>

<script>
    import apiService from '@/services/api-comunicados';
    import ContainerTable from '@/components/backend/common/ContainerTable';
    import BorrarModalComponent from '@/components/backend/components/BorrarModalComponent';
    import MainContentSearchable from '@/components/backend/components/MainContentSearchable';
    import OpcionesDropdownTableComponent from '@/components/backend/components/OpcionesDropdownTableComponent';
    import ModalComponent from '@/components/backend/components/ModalComponent';

    export default {
        data() {
            return {
                loading: false,
                fields: [
                    { key: "email", label: "Email", class: "text-center" },
                    { key: "nombre", label: "Nombre", class: "text-center" },
                    { key: "apellidos", label: "Apellidos", class: "text-center" },
                    { key: "telefono", label: "Teléfono", class: "text-center" },
                    { key: "activo", label: "Activo", class: "text-center" },
                    { key: "opciones", label: "opciones" }
                ],
                filters: {
                    search: '',
                    per_page: 10,
                    page: 1
                },
                filterGrupos: [],
                grupos: [],
                subscriptores: null,
                exportado: false,
                exportFormat: 'excel'
            }
        },
        created() {
            this.getSubscriptores();
            this.getGrupos();
            this.debouncedGetSubscriptores = _.debounce(this.getSubscriptores, 500);
        },
        methods: {
            async getSubscriptores() {
                if(this.filterGrupos.length > 0) {
                    this.filters.grupos_ids = this.filterGrupos;
                } else {
                    this.filters.grupos_ids = null;
                }

                this.loading = true;
                try {
                    this.subscriptores = await apiService.getSubscriptores(this.filters);
                } catch (error) {
                    console.error('Error al obtener subscriptores', error);
                } finally {
                    this.loading = false;
                }
            },
            async eliminarSubscriptor(subscriptor_id) {
                try {
                    await apiService.deleteSubscriptor(subscriptor_id);
                    this.$toast.success('Subscriptor eliminado correctamente');
                    this.closeModal('borrar-' + subscriptor_id, true)
                } catch (error) {
                    this.$toast.error(error.response?.data?.message);
                }
            },
            async getGrupos() {
                try {
                    const response = await apiService.getGrupos({ toSelect: true });
                    this.grupos = response.data;
                } catch (error) {
                    console.error('Error al obtener grupos', error);
                }
            },
            async exportarSubscriptores() {
                try {
                    await apiService.exportarSubscriptores({tipo: this.exportFormat});
                    this.exportado = true
                } catch (error) {
                    this.$toast.error(error.response?.data?.message);
                }
            },
            showModal(modal) {
                this.$modal.show(modal);
            },
            closeModal(modal, creado) {
                this.$modal.hide(modal);
                if (creado) {
                    this.getSubscriptores();
                }
            }
        },
        computed: {
            esMovil() {
                return window.innerWidth <= 768;
            }
        },
        components: {
            ContainerTable, BorrarModalComponent, MainContentSearchable, OpcionesDropdownTableComponent, ModalComponent
        }
    }
</script>